import { useCallback, useEffect, useRef, useState } from 'react';

// scroll offsets come back as sub-pixel fractions, so the ends need a tolerance
const DEFAULT_TOLERANCE_PX = 1;

export interface ScrollEdges {
  start: boolean;
  end: boolean;
}

interface UseScrollEdgesOptions {
  axis: 'horizontal' | 'vertical';
  tolerancePx?: number;
  /** re-measure when the content changes, not just its box */
  resyncOn?: unknown;
}

export const useScrollEdges = <T extends HTMLElement>({
  axis,
  tolerancePx = DEFAULT_TOLERANCE_PX,
  resyncOn,
}: UseScrollEdgesOptions) => {
  const ref = useRef<T | null>(null);
  const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false });

  const syncEdges = useCallback(() => {
    const node = ref.current;
    if (!node) return;

    const isHorizontal = axis === 'horizontal';
    const offset = isHorizontal ? node.scrollLeft : node.scrollTop;
    const visible = isHorizontal ? node.clientWidth : node.clientHeight;
    const total = isHorizontal ? node.scrollWidth : node.scrollHeight;
    const scrollable = total > visible + tolerancePx;

    setEdges({
      start: scrollable && offset > tolerancePx,
      end: scrollable && offset + visible < total - tolerancePx,
    });
  }, [axis, tolerancePx]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    syncEdges();
    const observer = new ResizeObserver(syncEdges);
    observer.observe(node);
    return () => observer.disconnect();
  }, [syncEdges, resyncOn]);

  return { ref, edges, syncEdges };
};
